/**
 * Template Comparison Modal - Enterprise-grade side-by-side analysis
 * Tab 1: Data Comparison — metrics table
 * Tab 2: AI Strategic Intelligence — LLM-powered deep analysis
 */
import React, { useState, useEffect } from 'react';
import {
  X, Download, TrendingDown, DollarSign, Calendar, Target,
  Award, Sparkles, Loader2, CheckCircle, Users, Star, Shield,
  Zap, Brain, BarChart3, AlertTriangle, ArrowRight, Lightbulb,
  Globe, TrendingUp, Building2, Clock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { apiFetch } from '../../services/api_service';


function fmt$(v) {
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(0)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${Math.round(v)}`;
}

const COL_BG = ['bg-violet-600', 'bg-blue-600', 'bg-emerald-600'];
const TAB_LIST = [
  { id: 'metrics', label: 'Side-by-Side Metrics', icon: BarChart3 },
  { id: 'ai', label: 'AI Strategic Intelligence', icon: Brain },
];

export default function TemplateComparisonModal({ templateIds, onClose, onUse }) {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [tab, setTab] = useState('metrics');

  // AI analysis state
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  // Fetch comparison data
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch(`/v1/simulations/templates/compare`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ templateIds }),
        });
        if (!res || res._error) throw new Error(res?.message || 'Comparison failed');
        setData(res);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [templateIds]);

  // Fetch AI analysis when tab switches
  const fetchAiAnalysis = async () => {
    if (aiAnalysis || aiLoading) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const d = await apiFetch(`/v1/simulations/templates/compare/ai-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ templateIds }),
      });
      setAiAnalysis(d.analysis);
    } catch (e) {
      setAiError(e.message);
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'ai' && !aiAnalysis && !aiLoading) {
      fetchAiAnalysis();
    }
  }, [tab]);

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      // PDF stream — raw fetch, apiFetch JSON-parses every body
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/v1/simulations/templates/compare/export-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({ comparison: data }),
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `template_comparison_${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: 'PDF Downloaded' });
    } catch (e) {
      toast({ title: 'Export Failed', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  if (loading) return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center" data-testid="comparison-loading">
      <div className="bg-[#0C0C0C] border border-[#1F1F1F] rounded-2xl p-10 text-center">
        <Loader2 className="w-10 h-10 text-violet-400 animate-spin mx-auto mb-3" />
        <p className="text-sm text-[#A0A0A0]">Analyzing templates...</p>
      </div>
    </div>
  );

  if (error || !data) return null;

  const { templates, analysis, summary } = data;
  const rec = analysis.recommendation;

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="comparison-modal">
      <div className="bg-[#0C0C0C] border border-[#1F1F1F] rounded-2xl max-w-6xl w-full my-4 overflow-hidden flex flex-col max-h-[92vh]" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="shrink-0 bg-gradient-to-r from-violet-900/20 to-emerald-900/15 border-b border-[#1F1F1F] px-6 py-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2" data-testid="comparison-title">
                <Target className="w-5 h-5 text-violet-400" /> Template Comparison
              </h2>
              <div className="flex items-center gap-4 mt-1.5 text-[10px] text-[#6B6B6B]">
                <span><DollarSign className="w-3 h-3 inline" /> {fmt$(summary.costRange.min)} - {fmt$(summary.costRange.max)}</span>
                <span><TrendingDown className="w-3 h-3 inline" /> {summary.reductionRange.min}% - {summary.reductionRange.max}%</span>
                <span><Calendar className="w-3 h-3 inline" /> {summary.timelineRange.earliest} - {summary.timelineRange.latest}</span>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#1F1F1F]" data-testid="close-comparison">
              <X className="w-5 h-5 text-[#6B6B6B]" />
            </button>
          </div>
        </div>

        {/* AI Recommendation Banner */}
        {rec && (
          <div className="shrink-0 bg-amber-500/5 border-b border-amber-500/15 px-6 py-2.5 flex items-center gap-2" data-testid="comparison-recommendation">
            <Award className="w-4 h-4 text-amber-400" />
            <span className="text-xs text-[#A0A0A0]">Recommended: <span className="text-white font-semibold">{rec.templateName}</span> — {rec.reason}</span>
          </div>
        )}

        {/* Tab Bar */}
        <div className="shrink-0 border-b border-[#1F1F1F] px-6 flex items-center gap-1">
          {TAB_LIST.map(t_ => {
            const Icon = t_.icon;
            return (
              <button
                key={t_.id}
                onClick={() => setTab(t_.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                  tab === t_.id
                    ? 'border-violet-500 text-violet-400'
                    : 'border-transparent text-[#6B6B6B] hover:text-white'
                }`}
                data-testid={`comparison-tab-${t_.id}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t_.label}
                {t_.id === 'ai' && !aiAnalysis && !aiLoading && (
                  <span className="ml-1 px-1.5 py-0.5 bg-violet-500/20 text-violet-400 text-[8px] font-bold rounded">AI</span>
                )}
                {t_.id === 'ai' && aiLoading && (
                  <Loader2 className="w-3 h-3 animate-spin text-violet-400" />
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto">
          {tab === 'metrics' && (
            <MetricsTab templates={templates} analysis={analysis} onUse={onUse} rec={rec} />
          )}
          {tab === 'ai' && (
            <AIStrategicTab
              aiAnalysis={aiAnalysis}
              aiLoading={aiLoading}
              aiError={aiError}
              templates={templates}
              onRetry={fetchAiAnalysis}
              onUse={onUse}
            />
          )}
        </div>

        {/* Quick Insights Bar */}
        <div className="shrink-0 border-t border-[#1F1F1F] px-6 py-3 bg-[#0A0A0A]">
          <div className="grid grid-cols-3 gap-3">
            <InsightCard
              icon={<DollarSign className="w-3.5 h-3.5 text-blue-400" />}
              title="Cost"
              text={`${analysis.costComparison.cheapest.templateName} is ${analysis.costComparison.costDifferencePercent}% cheaper`}
            />
            <InsightCard
              icon={<TrendingDown className="w-3.5 h-3.5 text-emerald-400" />}
              title="Impact"
              text={`${analysis.emissionsComparison.highest.templateName} achieves ${analysis.emissionsComparison.highest.reduction}% reduction`}
            />
            <InsightCard
              icon={<Calendar className="w-3.5 h-3.5 text-purple-400" />}
              title="Timeline"
              text={analysis.timelineComparison.yearsDifference > 0
                ? `${analysis.timelineComparison.fastest.templateName} completes ${analysis.timelineComparison.yearsDifference}yr earlier`
                : 'All templates target the same year'}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-[#1F1F1F] px-6 py-3 flex justify-between">
          <Button
            size="sm"
            disabled={exporting}
            className="bg-violet-600 hover:bg-violet-700 text-white gap-1.5 disabled:opacity-50"
            onClick={handleExportPDF}
            data-testid="export-comparison-pdf"
          >
            <Download className="w-3.5 h-3.5" />
            {exporting ? 'Generating...' : 'Export PDF'}
          </Button>
          <Button variant="outline" size="sm" className="border-[#2A2A2A] text-[#A0A0A0]" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}

/* ─── Metrics Tab (existing comparison table) ──────────────────────────── */

function MetricsTab({ templates, analysis, onUse, rec }) {
  return (
    <table className="w-full" data-testid="comparison-table">
      <thead className="sticky top-0 bg-[#0C0C0C] z-10">
        <tr className="border-b border-[#1F1F1F]">
          <th className="w-40 p-3" />
          {templates.map((t, i) => (
            <th key={t.templateId} className="p-3 text-left border-l border-[#1F1F1F]">
              <div className="flex items-center gap-2 mb-1.5">
                <span className={`w-6 h-6 rounded-full ${COL_BG[i]} flex items-center justify-center text-[10px] font-bold text-white`}>{i + 1}</span>
                {rec?.templateId === t.templateId && <Award className="w-4 h-4 text-amber-400" />}
              </div>
              <p className="text-xs font-semibold text-white leading-tight">{t.templateName}</p>
              <div className="flex items-center gap-1.5 mt-1">
                <DiffBadge diff={t.difficulty} />
                <span className="text-[9px] text-[#4A4A4A]">{t.industry.replace(/_/g, ' ')}</span>
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-[#1A1A1A] text-xs">
        <SectionHeader label="Emissions Impact" />
        <Row label="Total Reduction" values={templates.map(t => `${t.expectedResults.emissionsReduction.percentageReduction}%`)} best="high" />
        <Row label="Net-Zero Year" values={templates.map(t => t.expectedResults.timeline.netZeroAchieved || '—')} />

        <SectionHeader label="Financial" />
        <Row label="Investment Range" values={templates.map(t => {
          const c = t.expectedResults.financialSummary.totalCostRange;
          return `${fmt$(c.min)} - ${fmt$(c.max)}`;
        })} best="low" />
        <Row label="Avg. Investment" values={templates.map(t => {
          const c = t.expectedResults.financialSummary.totalCostRange;
          return fmt$((c.min + c.max) / 2);
        })} best="low" />

        <SectionHeader label="Timeline" />
        <Row label="Target Year" values={templates.map(t => String(t.target.targetYear))} best="low" />
        <Row label="Target Type" values={templates.map(t => t.target.type.replace(/_/g, ' '))} />

        <SectionHeader label="Industry Benchmark" />
        <Row label="Peer Adoption" values={templates.map(t => `${Math.round(t.industryBenchmark.peerAdoptionRate * 100)}%`)} best="high" />
        <Row label="Avg. Industry Investment" values={templates.map(t => fmt$(t.industryBenchmark.avgInvestment || 0))} />
        <Row label="Leading Companies" values={templates.map(t => (t.industryBenchmark.leadingCompanies || []).slice(0, 3).join(', '))} />

        <SectionHeader label="Implementation" />
        <Row label="Actions Required" values={templates.map(t => String((t.actionIds || []).length))} />
        <Row label="Difficulty" values={templates.map(t => t.difficulty)} type="difficulty" />

        <SectionHeader label="Adoption" />
        <Row label="Companies Using" values={templates.map(t => (t.popularity || 0).toLocaleString())} best="high" />
        <Row label="Avg. Rating" values={templates.map(t => `${(t.avgRating || 0).toFixed(1)} / 5`)} best="high" />

        <tr>
          <td className="p-3" />
          {templates.map((t, i) => (
            <td key={t.templateId} className="p-3 border-l border-[#1A1A1A]">
              <Button size="sm" className={`w-full ${COL_BG[i]} hover:opacity-90 text-white gap-1.5`} onClick={() => onUse(t.templateId)} data-testid={`compare-use-${t.templateId}`}>
                <Sparkles className="w-3 h-3" /> Use Template
              </Button>
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}

/* ─── AI Strategic Intelligence Tab ────────────────────────────────────── */

function AIStrategicTab({ aiAnalysis, aiLoading, aiError, templates, onRetry, onUse }) {
  if (aiLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-8" data-testid="ai-loading">
        <div className="relative mb-4">
          <div className="w-16 h-16 rounded-2xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
            <Brain className="w-8 h-8 text-violet-400 animate-pulse" />
          </div>
          <Loader2 className="w-5 h-5 text-violet-400 animate-spin absolute -top-1 -right-1" />
        </div>
        <p className="text-sm font-medium text-white mb-1">Generating Strategic Intelligence...</p>
        <p className="text-xs text-[#6B6B6B] text-center max-w-md">
          Claude Sonnet is analyzing {templates.length} templates across cost, risk, regulatory alignment, competitive positioning, and implementation feasibility
        </p>
      </div>
    );
  }

  if (aiError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-8" data-testid="ai-error">
        <AlertTriangle className="w-10 h-10 text-amber-400 mb-3" />
        <p className="text-sm text-[#A0A0A0] mb-3">AI analysis unavailable</p>
        <Button size="sm" className="bg-violet-600 text-white gap-1.5" onClick={onRetry}>
          <Sparkles className="w-3.5 h-3.5" /> Retry Analysis
        </Button>
      </div>
    );
  }

  if (!aiAnalysis) return null;

  const a = aiAnalysis;
  const templateMap = Object.fromEntries(templates.map(t => [t.templateId, t]));

  return (
    <div className="p-6 space-y-6" data-testid="ai-strategic-tab">
      {/* Executive Summary */}
      <div className="bg-gradient-to-r from-violet-500/5 to-blue-500/5 border border-violet-500/20 rounded-xl p-5" data-testid="executive-summary">
        <div className="flex items-center gap-2 mb-2">
          <Brain className="w-4 h-4 text-violet-400" />
          <h3 className="text-sm font-bold text-white">Executive Summary</h3>
          <Badge className="bg-violet-500/20 text-violet-400 border border-violet-500/30 text-[8px]">AI-GENERATED</Badge>
        </div>
        <p className="text-sm text-[#C0C0C0] leading-relaxed">{a.executiveSummary}</p>
      </div>

      {/* Strategic Recommendation */}
      {a.strategicRecommendation && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5" data-testid="strategic-recommendation">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-amber-400" />
              <h3 className="text-sm font-bold text-white">Strategic Recommendation</h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-[#6B6B6B]">Confidence</span>
              <span className="text-sm font-bold text-amber-400">{a.strategicRecommendation.confidence}%</span>
            </div>
          </div>
          <div className="flex items-center gap-3 mb-3">
            <div className="px-3 py-1.5 bg-amber-500/15 border border-amber-500/30 rounded-lg">
              <span className="text-xs font-bold text-amber-400">{a.strategicRecommendation.templateName}</span>
            </div>
          </div>
          <p className="text-sm text-[#A0A0A0] leading-relaxed">{a.strategicRecommendation.reasoning}</p>
          {a.strategicRecommendation.templateId && (
            <Button
              size="sm"
              className="mt-3 bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
              onClick={() => onUse(a.strategicRecommendation.templateId)}
              data-testid="ai-use-recommended"
            >
              <Sparkles className="w-3.5 h-3.5" /> Use Recommended Template
            </Button>
          )}
        </div>
      )}

      {/* Template-by-Template Analysis */}
      {a.templateAnalyses?.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider mb-3">Template Analysis</h3>
          <div className={`grid grid-cols-${Math.min(a.templateAnalyses.length, 3)} gap-4`}>
            {a.templateAnalyses.map((ta, i) => {
              const tmpl = templateMap[ta.templateId];
              return (
                <div key={ta.templateId || i} className="bg-[#111] border border-[#1F1F1F] rounded-xl p-4 space-y-3" data-testid={`ai-template-analysis-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full ${COL_BG[i]} flex items-center justify-center text-[10px] font-bold text-white`}>{i + 1}</span>
                      <span className="text-xs font-semibold text-white truncate">{tmpl?.templateName || ta.templateId}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] text-[#6B6B6B]">Fit</span>
                      <span className={`text-sm font-bold ${ta.strategicFitScore >= 80 ? 'text-emerald-400' : ta.strategicFitScore >= 60 ? 'text-amber-400' : 'text-red-400'}`}>
                        {ta.strategicFitScore}
                      </span>
                    </div>
                  </div>

                  {/* Score bar */}
                  <div className="h-1.5 bg-[#1F1F1F] rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${ta.strategicFitScore >= 80 ? 'bg-emerald-500' : ta.strategicFitScore >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${ta.strategicFitScore}%` }}
                    />
                  </div>

                  {/* Strengths */}
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <CheckCircle className="w-3 h-3 text-emerald-400" />
                      <span className="text-[9px] font-semibold text-emerald-400 uppercase">Strengths</span>
                    </div>
                    <ul className="space-y-0.5">
                      {(ta.strengths || []).map((s, j) => (
                        <li key={j} className="text-[10px] text-[#A0A0A0] pl-3 relative before:absolute before:left-0 before:top-1.5 before:w-1 before:h-1 before:bg-emerald-500/50 before:rounded-full">{s}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Risks */}
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <AlertTriangle className="w-3 h-3 text-amber-400" />
                      <span className="text-[9px] font-semibold text-amber-400 uppercase">Risks</span>
                    </div>
                    <ul className="space-y-0.5">
                      {(ta.risks || []).map((r, j) => (
                        <li key={j} className="text-[10px] text-[#A0A0A0] pl-3 relative before:absolute before:left-0 before:top-1.5 before:w-1 before:h-1 before:bg-amber-500/50 before:rounded-full">{r}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Meta badges */}
                  <div className="space-y-1.5 pt-1 border-t border-[#1F1F1F]">
                    <MetaBadge icon={Shield} label="Regulatory" value={ta.regulatoryAlignment} />
                    <MetaBadge icon={Building2} label="Board-Ready" value={ta.boardReadiness} />
                    <MetaBadge icon={Zap} label="Quick Win" value={ta.quickWin} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Risk Matrix */}
      {a.riskMatrix && (
        <div className="bg-[#111] border border-[#1F1F1F] rounded-xl p-5" data-testid="risk-matrix">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-blue-400" />
            <h3 className="text-sm font-bold text-white">Risk-Reward Matrix</h3>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <RiskCard
              color="emerald"
              icon={Shield}
              label="Lowest Risk"
              name={a.riskMatrix.lowestRisk?.templateId}
              reason={a.riskMatrix.lowestRisk?.reason}
              templates={templates}
            />
            <RiskCard
              color="amber"
              icon={TrendingUp}
              label="Highest Reward"
              name={a.riskMatrix.highestReward?.templateId}
              reason={a.riskMatrix.highestReward?.reason}
              templates={templates}
            />
            <RiskCard
              color="violet"
              icon={Award}
              label="Best Risk-Reward"
              name={a.riskMatrix.bestRiskReward?.templateId}
              reason={a.riskMatrix.bestRiskReward?.reason}
              templates={templates}
            />
          </div>
        </div>
      )}

      {/* Implementation Roadmap */}
      {a.implementationRoadmap && (
        <div className="bg-[#111] border border-[#1F1F1F] rounded-xl p-5" data-testid="implementation-roadmap">
          <div className="flex items-center gap-2 mb-3">
            <ArrowRight className="w-4 h-4 text-emerald-400" />
            <h3 className="text-sm font-bold text-white">Implementation Roadmap</h3>
          </div>

          {/* 90-day start */}
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 mb-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Clock className="w-3 h-3 text-emerald-400" />
              <span className="text-[10px] font-bold text-emerald-400 uppercase">90-Day Quick Start</span>
            </div>
            <p className="text-xs text-[#C0C0C0]">{a.implementationRoadmap.recommended90DayStart}</p>
          </div>

          {/* Milestones */}
          <div className="mb-3">
            <span className="text-[10px] font-semibold text-[#6B6B6B] uppercase">Key Milestones</span>
            <div className="mt-1.5 space-y-1.5">
              {(a.implementationRoadmap.keyMilestones || []).map((m, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="mt-0.5 w-5 h-5 rounded-full bg-[#1F1F1F] border border-[#2A2A2A] flex items-center justify-center shrink-0">
                    <span className="text-[8px] font-bold text-[#6B6B6B]">{i + 1}</span>
                  </div>
                  <span className="text-xs text-[#A0A0A0]">{m}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Dependencies */}
          <div>
            <span className="text-[10px] font-semibold text-[#6B6B6B] uppercase">Critical Dependencies</span>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {(a.implementationRoadmap.criticalDependencies || []).map((d, i) => (
                <span key={i} className="px-2 py-1 bg-amber-500/10 border border-amber-500/20 rounded-lg text-[10px] text-amber-400">{d}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Competitive Intelligence */}
      {a.competitiveIntelligence && (
        <div className="bg-[#111] border border-[#1F1F1F] rounded-xl p-5" data-testid="competitive-intelligence">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-4 h-4 text-blue-400" />
            <h3 className="text-sm font-bold text-white">Competitive Intelligence</h3>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <CICard icon={TrendingUp} label="Industry Trend" text={a.competitiveIntelligence.industryTrend} />
            <CICard icon={Zap} label="First-Mover Advantage" text={a.competitiveIntelligence.firstMoverAdvantage} />
            <CICard icon={Users} label="Stakeholder Impact" text={a.competitiveIntelligence.stakeholderImpact} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Shared Sub-components ────────────────────────────────────────────── */

function SectionHeader({ label }) {
  return (
    <tr className="bg-[#0F0F0F]">
      <td colSpan={4} className="px-3 py-2 text-[9px] uppercase tracking-wider font-semibold text-[#4A4A4A]">{label}</td>
    </tr>
  );
}

function Row({ label, values, best, type }) {
  let bestIdx = -1;
  if (best && values.length > 1) {
    const nums = values.map(v => { const n = parseFloat(String(v).replace(/[^0-9.-]/g, '')); return isNaN(n) ? null : n; });
    const valid = nums.filter(n => n !== null);
    if (valid.length === nums.length) {
      const target = best === 'high' ? Math.max(...valid) : Math.min(...valid);
      bestIdx = nums.indexOf(target);
    }
  }
  return (
    <tr className="hover:bg-[#111] transition-colors">
      <td className="px-3 py-2 text-[#6B6B6B] font-medium">{label}</td>
      {values.map((v, i) => (
        <td key={i} className="px-3 py-2 border-l border-[#1A1A1A]">
          {type === 'difficulty' ? <DiffBadge diff={v} /> : (
            <span className={i === bestIdx ? 'text-emerald-400 font-semibold' : 'text-white'}>
              {v}
              {i === bestIdx && <span className="ml-1.5 text-[8px] bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-1 py-0.5 rounded">BEST</span>}
            </span>
          )}
        </td>
      ))}
    </tr>
  );
}

function DiffBadge({ diff }) {
  const m = { beginner: ['text-emerald-400', 'bg-emerald-500/15', 'border-emerald-500/30'], intermediate: ['text-amber-400', 'bg-amber-500/15', 'border-amber-500/30'], advanced: ['text-red-400', 'bg-red-500/15', 'border-red-500/30'] };
  const [tc, bg, bc] = m[diff] || m.intermediate;
  return <Badge className={`${bg} ${tc} border ${bc} text-[9px]`}>{diff}</Badge>;
}

function InsightCard({ icon, title, text }) {
  return (
    <div className="bg-[#111] border border-[#1F1F1F] rounded-lg p-2.5">
      <div className="flex items-center gap-1.5 mb-1">{icon}<span className="text-[10px] font-semibold text-[#A0A0A0]">{title}</span></div>
      <p className="text-[10px] text-[#6B6B6B] leading-relaxed">{text}</p>
    </div>
  );
}

function MetaBadge({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-1.5">
      <Icon className="w-3 h-3 text-[#6B6B6B] mt-0.5 shrink-0" />
      <div>
        <span className="text-[8px] text-[#4A4A4A] uppercase">{label}</span>
        <p className="text-[10px] text-[#A0A0A0]">{value}</p>
      </div>
    </div>
  );
}

function RiskCard({ color, icon: Icon, label, name, reason, templates }) {
  const tmpl = templates.find(t => t.templateId === name);
  return (
    <div className={`bg-${color}-500/5 border border-${color}-500/20 rounded-lg p-3`}>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className={`w-3.5 h-3.5 text-${color}-400`} />
        <span className={`text-[10px] font-bold text-${color}-400 uppercase`}>{label}</span>
      </div>
      <p className="text-xs font-semibold text-white mb-1">{tmpl?.templateName || name}</p>
      <p className="text-[10px] text-[#A0A0A0] leading-relaxed">{reason}</p>
    </div>
  );
}

function CICard({ icon: Icon, label, text }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 text-blue-400" />
        <span className="text-[10px] font-bold text-blue-400 uppercase">{label}</span>
      </div>
      <p className="text-[10px] text-[#A0A0A0] leading-relaxed">{text}</p>
    </div>
  );
}
